import React from "react";
import { useState, useMemo, useCallback, useRef } from "react";
import { Image, Video, Search, Square, RectangleVertical, RectangleHorizontal, AlertTriangle, Info, X, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useGroupedCreatives, groupCreatives, type ModalRow } from "@/hooks/useGroupedCreatives";
import { generateAdName, type LoadCreative, type GenerateAdNameOptions } from "@/hooks/useLoadCreatives";
import type { AdFormat, CarouselCard, FlexibleAsset } from "@/types/bulkAd";
import { COLLECTION_HEADLINE_MACROS, COLLECTION_DESCRIPTION_MACROS } from "@/types/bulkAd";
import { cn } from "@/lib/utils";

// ===== Output types for each format =====

export interface SingleRowOutput {
  ad_format: AdFormat;
  ad_name: string;
  creative_feed: string | null;
  meta_creative_id_feed: string | null;
  creative_vertical: string | null;
  meta_creative_id_vertical: string | null;
  creative_horizontal: string | null;
  meta_creative_id_horizontal: string | null;
  thumbnail_url: string | null;
}

export interface CarouselRowOutput {
  ad_format: "Carousel";
  ad_name: string;
  carousel_data: { cards: CarouselCard[] };
  thumbnail_url: string | null;
}

export interface CollectionRowOutput {
  ad_format: "Collection";
  ad_name: string;
  collection_creative_name: string;
  collection_meta_creative_id: string;
  thumbnail_url: string | null;
  instant_experience: {
    label: string;
    product_headline: string;
    product_description: string;
  };
}

export interface FlexibleRowOutput {
  ad_format: "Flexible";
  ad_name: string;
  flexible_data: { assets: FlexibleAsset[]; assetType: "image" | "video" };
  thumbnail_url: string | null;
}

export type LoadCreativesOutput = SingleRowOutput | CarouselRowOutput | CollectionRowOutput | FlexibleRowOutput;

// ===== Selection mode per format =====

type SelectionMode = "checkbox" | "numbered";

function getSelectionMode(format: AdFormat | null): SelectionMode {
  switch (format) {
    case "Carousel": return "numbered";
    default: return "checkbox"; // Single Image or Video, Collection, Flexible, Catalog, null
  }
}

function getMinMax(format: AdFormat | null): { min: number; max: number } {
  switch (format) {
    case "Carousel": return { min: 2, max: 10 };
    case "Flexible": return { min: 2, max: 10 };
    default: return { min: 1, max: Infinity }; // Single, Collection, Catalog — no limit
  }
}

// ===== Format selector options =====

type ModalFormat = "Single Image or Video" | "Carousel" | "Collection" | "Flexible";

const FORMAT_OPTIONS: { value: ModalFormat; label: string }[] = [
  { value: "Single Image or Video", label: "Single Image or Video" },
  { value: "Carousel", label: "Carousel" },
  { value: "Collection", label: "Collection" },
  { value: "Flexible", label: "Flexible" },
];

const FORMAT_HELPER_TEXT: Record<ModalFormat, string> = {
  "Single Image or Video": "One ad per creative. Each creative becomes its own row.",
  "Carousel": "2–10 cards in one ad. Each creative becomes a card.",
  "Collection": "Select one hero creative. Catalog assigned in the sheet.",
  "Flexible": "2–10 assets, Meta picks the best combo per person.",
};

// ===== Saved multi-ad types =====
interface SavedCarousel {
  id: string;
  orderedIds: string[];
}

interface SavedFlexibleAd {
  id: string;
  selectedIds: string[];
}

// ===== Helper: build individual (ungrouped) ModalRows from raw creatives =====
function buildIndividualRows(allCreatives: LoadCreative[]): ModalRow[] {
  return allCreatives.map((c) => {
    const slot = classifySlotForIndividual(c);
    return {
      id: c.id,
      displayName: c.filename.replace(/\.[^/.]+$/, ""),
      type: (c.creative_type === "video" ? "video" : "image") as "image" | "video",
      coverage: "Single" as const,
      thumbnailUrl: c.thumbnail_url,
      variants: {
        feed: slot === "feed" ? c : null,
        vertical: slot === "vertical" ? c : null,
        horizontal: slot === "horizontal" ? c : null,
      },
      members: [c],
      isGrouped: false,
    };
  }).sort((a, b) => {
    const aDate = a.members[0]?.created_at ? new Date(a.members[0].created_at).getTime() : 0;
    const bDate = b.members[0]?.created_at ? new Date(b.members[0].created_at).getTime() : 0;
    return bDate - aDate;
  });
}

function classifySlotForIndividual(creative: LoadCreative): "feed" | "vertical" | "horizontal" {
  if (creative.aspect_ratio === "vertical") return "vertical";
  if (creative.aspect_ratio === "horizontal") return "horizontal";
  if (creative.aspect_ratio === "feed" || creative.aspect_ratio === "square") return "feed";
  if (!creative.dimensions) return "feed";
  const parts = creative.dimensions.split("x").map(Number);
  if (parts.length !== 2 || !parts[0] || !parts[1]) return "feed";
  const ratio = parts[0] / parts[1];
  if (ratio >= 0.52 && ratio <= 0.60) return "vertical";
  if (ratio >= 1.73 && ratio <= 1.83) return "horizontal";
  return "feed";
}

// ===== Props =====

interface LoadCreativesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  adAccountId: string | undefined;
  userId: string | undefined;
  /** Called for Single format rows (backward compatible) */
  onLoadRows: (rows: SingleRowOutput[]) => void;
  /** Called for all format types (new) */
  onLoadFormatRows?: (rows: LoadCreativesOutput[]) => void;
  /** The ad format to load creatives for. Defaults to "Single Image or Video" */
  adFormat?: AdFormat | null;
}

// ===== Variant Badge Component =====

function VariantBadge({ slot, present }: { slot: "feed" | "vertical" | "horizontal"; present: boolean }) {
  if (!present) return null;

  const config = {
    feed: {
      icon: Square,
      label: "■",
      tooltip: "Feed / Explore (1080×1080)",
      className: "bg-primary/15 text-primary border-primary/30",
    },
    vertical: {
      icon: RectangleVertical,
      label: "▮",
      tooltip: "Stories / Reels (1080×1920)",
      className: "bg-violet-500/15 text-violet-600 dark:text-violet-400 border-violet-500/30",
    },
    horizontal: {
      icon: RectangleHorizontal,
      label: "▬",
      tooltip: "Search / Right Column (1200×628)",
      className: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
    },
  };

  const c = config[slot];

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex items-center justify-center w-5 h-5 rounded text-[10px] font-bold border",
              c.className
            )}
          >
            {slot === "feed" ? "■" : slot === "vertical" ? "▮" : "▬"}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {c.tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ===== Main Component =====

export function LoadCreativesModal({
  open,
  onOpenChange,
  adAccountId,
  userId,
  onLoadRows,
  onLoadFormatRows,
  adFormat: propFormat,
}: LoadCreativesModalProps) {
  // Internal format state (user can switch via pills)
  const [internalFormat, setInternalFormat] = useState<ModalFormat>(
    (propFormat as ModalFormat) ?? "Single Image or Video"
  );
  const [pendingFormat, setPendingFormat] = useState<ModalFormat | null>(null);

  const effectiveFormat = internalFormat;
  const selectionMode = getSelectionMode(effectiveFormat);
  const { min, max } = getMinMax(effectiveFormat);

  const { modalRows, allCreatives, isLoading } = useGroupedCreatives(adAccountId, userId);

  // Fix 1: For Collection & Flexible, show individual files (not groups)
  const individualRows = useMemo(() => buildIndividualRows(allCreatives), [allCreatives]);

  const displayRows = useMemo(() => {
    if (effectiveFormat === "Collection" || effectiveFormat === "Flexible") {
      return individualRows;
    }
    return modalRows;
  }, [effectiveFormat, modalRows, individualRows]);
  
  // For checkbox/radio: Set<modalRow.id>
  // For numbered (Carousel): ordered array of modalRow.id
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [orderedIds, setOrderedIds] = useState<string[]>([]); // For Carousel ordering
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "image" | "video">("all");
  const [coverageFilter, setCoverageFilter] = useState<"all" | "Triple" | "Pair" | "Single">("all");
  const [isConfirming, setIsConfirming] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false); // >50 rows confirmation

  // Multi-ad state
  const [savedCarousels, setSavedCarousels] = useState<SavedCarousel[]>([]);
  const [savedFlexibles, setSavedFlexibles] = useState<SavedFlexibleAd[]>([]);

  // Instant Experience settings for Collection mode
  const [ieLabel, setIeLabel] = useState("Shop Now");
  const [ieProductHeadline, setIeProductHeadline] = useState("{{product.name}}");
  const [ieProductDescription, setIeProductDescription] = useState("{{product.current_price}}");

  // Handle format switch request
  const handleFormatSwitch = useCallback((newFormat: ModalFormat) => {
    if (newFormat === internalFormat) return;
    if (selectedIds.size > 0 || savedCarousels.length > 0 || savedFlexibles.length > 0) {
      setPendingFormat(newFormat);
    } else {
      setInternalFormat(newFormat);
    }
  }, [internalFormat, selectedIds.size, savedCarousels.length, savedFlexibles.length]);

  const confirmFormatSwitch = useCallback(() => {
    if (pendingFormat) {
      setSelectedIds(new Set());
      setOrderedIds([]);
      setSavedCarousels([]);
      setSavedFlexibles([]);
      setInternalFormat(pendingFormat);
      setPendingFormat(null);
    }
  }, [pendingFormat]);

  const cancelFormatSwitch = useCallback(() => {
    setPendingFormat(null);
  }, []);

  // Reset state when modal closes
  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setSelectedIds(new Set());
      setOrderedIds([]);
      setSavedCarousels([]);
      setSavedFlexibles([]);
      setSearch("");
      setTypeFilter("all");
      setCoverageFilter("all");
      setInternalFormat((propFormat as ModalFormat) ?? "Single Image or Video");
      setPendingFormat(null);
      setIeLabel("Shop Now");
      setIeProductHeadline("product_name");
      setIeProductDescription("current_price");
    }
    onOpenChange(open);
  }, [onOpenChange, propFormat]);

  // Filtered rows — use displayRows instead of modalRows
  const filtered = useMemo(() => {
    return displayRows.filter((row) => {
      if (search && !row.displayName.toLowerCase().includes(search.toLowerCase())) return false;
      if (typeFilter !== "all" && row.type !== typeFilter) return false;
      if (coverageFilter !== "all" && row.coverage !== coverageFilter) return false;
      return true;
    });
  }, [displayRows, search, typeFilter, coverageFilter]);

  // Toggle selection
  const toggleSelection = useCallback((rowId: string) => {
    if (selectionMode === "numbered") {
      // Carousel: toggle in ordered list, respect max
      setOrderedIds((prev) => {
        if (prev.includes(rowId)) {
          const next = prev.filter((id) => id !== rowId);
          setSelectedIds(new Set(next));
          return next;
        }
        if (prev.length >= max) {
          toast.info(`Maximum ${max} cards reached. Deselect a card to add a different one.`);
          return prev;
        }
        const next = [...prev, rowId];
        setSelectedIds(new Set(next));
        return next;
      });
      return;
    }

    // Checkbox: multi-select (with max enforcement for Flexible)
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) {
        next.delete(rowId);
      } else {
        if (max !== Infinity && next.size >= max) {
          toast.info(`Maximum ${max} assets reached. Deselect one to add a different one.`);
          return prev;
        }
        next.add(rowId);
      }
      return next;
    });
  }, [selectionMode, max]);

  // Select all (checkbox mode only)
  const toggleSelectAll = useCallback(() => {
    if (selectionMode !== "checkbox") return;
    const allFilteredIds = filtered.map((r) => r.id);
    const allSelected = allFilteredIds.every((id) => selectedIds.has(id));
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allFilteredIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allFilteredIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }, [filtered, selectedIds, selectionMode]);

  const allFilteredSelected = selectionMode === "checkbox" && filtered.length > 0 && filtered.every((r) => selectedIds.has(r.id));
  const someFilteredSelected = selectionMode === "checkbox" && filtered.some((r) => selectedIds.has(r.id)) && !allFilteredSelected;

  const selectionCount = selectedIds.size;
  const canConfirmCarousel = savedCarousels.length > 0 || (selectionCount >= min && selectionCount <= max);
  const canConfirmCollection = selectionCount >= 1;
  const canConfirmFlexible = savedFlexibles.length > 0 || (selectionCount >= min && selectionCount <= max);
  const canConfirm = effectiveFormat === "Carousel"
    ? canConfirmCarousel
    : effectiveFormat === "Collection"
      ? canConfirmCollection
      : effectiveFormat === "Flexible"
        ? canConfirmFlexible
        : (selectionCount >= min && selectionCount <= max);

  // Get ordered number for Carousel mode
  const getCarouselNumber = (rowId: string): number | null => {
    if (selectionMode !== "numbered") return null;
    const idx = orderedIds.indexOf(rowId);
    return idx >= 0 ? idx + 1 : null;
  };

  // Add current selection as a saved carousel
  const handleAddCarousel = useCallback(() => {
    if (orderedIds.length < 2) {
      toast.info("Select at least 2 cards to save a carousel.");
      return;
    }
    setSavedCarousels((prev) => [
      ...prev,
      { id: crypto.randomUUID(), orderedIds: [...orderedIds] },
    ]);
    setSelectedIds(new Set());
    setOrderedIds([]);
    toast.success(`Carousel ${savedCarousels.length + 1} saved`);
  }, [orderedIds, savedCarousels.length]);

  const handleRemoveSavedCarousel = useCallback((carouselId: string) => {
    setSavedCarousels((prev) => prev.filter((c) => c.id !== carouselId));
  }, []);

  // Add current selection as a saved flexible ad
  const handleAddFlexible = useCallback(() => {
    if (selectedIds.size < 2) {
      toast.info("Select at least 2 assets to save a flexible ad.");
      return;
    }
    setSavedFlexibles((prev) => [
      ...prev,
      { id: crypto.randomUUID(), selectedIds: Array.from(selectedIds) },
    ]);
    setSelectedIds(new Set());
    toast.success(`Flexible ${savedFlexibles.length + 1} saved`);
  }, [selectedIds, savedFlexibles.length]);

  const handleRemoveSavedFlexible = useCallback((flexId: string) => {
    setSavedFlexibles((prev) => prev.filter((f) => f.id !== flexId));
  }, []);

  // ===== Build output rows =====
  const handleConfirm = useCallback(async () => {
    if (!adAccountId || !userId) return;
    setIsConfirming(true);

    try {
      // Use displayRows for resolving IDs (matches what the user sees)
      const resolveRow = (id: string) => displayRows.find((r) => r.id === id);

      if (effectiveFormat === "Single Image or Video" || (effectiveFormat as string) === "Catalog") {
        const selectedRows = Array.from(selectedIds)
          .map(resolveRow)
          .filter(Boolean) as ModalRow[];

        const rows: SingleRowOutput[] = await Promise.all(
          selectedRows.map(async (row, i) => {
            const nameSource = row.variants.feed?.filename || row.variants.vertical?.filename || row.variants.horizontal?.filename || "";
            const adName = await generateAdName({
              filename: nameSource,
              adAccountId,
              userId,
              format: row.type === "video" ? "Single Video" : "Single Image",
              seq: selectedRows.length > 1 ? i + 1 : undefined,
            });
            return {
              ad_format: "Single Image or Video" as AdFormat,
              ad_name: adName,
              creative_feed: row.variants.feed?.filename || null,
              meta_creative_id_feed: row.variants.feed?.meta_creative_id || null,
              creative_vertical: row.variants.vertical?.filename || null,
              meta_creative_id_vertical: row.variants.vertical?.meta_creative_id || null,
              creative_horizontal: row.variants.horizontal?.filename || null,
              meta_creative_id_horizontal: row.variants.horizontal?.meta_creative_id || null,
              thumbnail_url: row.thumbnailUrl,
            };
          })
        );
        if (onLoadFormatRows) {
          onLoadFormatRows(rows);
        } else {
          onLoadRows(rows);
        }
      } else if (effectiveFormat === "Carousel") {
        // Build all carousels: saved ones + current selection (if ≥2 cards)
        const allCarousels = [...savedCarousels];
        if (orderedIds.length >= 2) {
          allCarousels.push({ id: crypto.randomUUID(), orderedIds: [...orderedIds] });
        }

        if (allCarousels.length === 0) return;

        const outputs: CarouselRowOutput[] = await Promise.all(
          allCarousels.map(async (carousel, carouselIdx) => {
            const selectedRows = carousel.orderedIds
              .map(resolveRow)
              .filter(Boolean) as ModalRow[];

            const cards: CarouselCard[] = selectedRows.map((row) => {
              const filename = row.variants.feed?.filename || row.members[0]?.filename || "";
              const ext = filename.toLowerCase().split(".").pop() || "";
              const creative_type: "image" | "video" = ["mp4", "mov", "webm"].includes(ext) ? "video" : "image";
              return {
                id: crypto.randomUUID(),
                creative_id: row.variants.feed?.id || row.members[0]?.id || "",
                creative_filename: filename,
                creative_filename_vertical: row.variants.vertical?.filename || null,
                meta_creative_id: row.variants.feed?.meta_creative_id || row.members[0]?.meta_creative_id || "",
                meta_creative_id_vertical: row.variants.vertical?.meta_creative_id || null,
                thumbnail_url: row.thumbnailUrl,
                headline: "",
                card_url: "",
                creative_type,
              };
            });

            const firstRow = selectedRows[0];
            const adName = await generateAdName({
              filename: firstRow?.displayName || "Carousel",
              adAccountId,
              userId,
              format: "Carousel",
              cardCount: selectedRows.length,
              seq: allCarousels.length > 1 ? carouselIdx + 1 : undefined,
            });

            return {
              ad_format: "Carousel" as const,
              ad_name: adName,
              carousel_data: { cards },
              thumbnail_url: firstRow?.thumbnailUrl || null,
            };
          })
        );

        if (onLoadFormatRows) {
          onLoadFormatRows(outputs);
        }
      } else if (effectiveFormat === "Collection") {
        // Each selected asset = 1 collection ad row
        const selectedRowIds = Array.from(selectedIds);
        if (selectedRowIds.length === 0) return;

        const outputs: CollectionRowOutput[] = await Promise.all(
          selectedRowIds.map(async (id, idx) => {
            const row = displayRows.find((r) => r.id === id);
            const creative = row?.variants.feed || row?.variants.vertical || row?.variants.horizontal || row?.members[0];
            const adName = await generateAdName({
              filename: creative?.filename || "Collection",
              adAccountId,
              userId,
              format: "Collection",
              seq: selectedRowIds.length > 1 ? idx + 1 : undefined,
            });

            return {
              ad_format: "Collection" as const,
              ad_name: adName,
              collection_creative_name: creative?.filename || "",
              collection_meta_creative_id: creative?.meta_creative_id || "",
              thumbnail_url: row?.thumbnailUrl || null,
              instant_experience: {
                label: ieLabel,
                product_headline: ieProductHeadline,
                product_description: ieProductDescription,
              },
            };
          })
        );

        if (onLoadFormatRows) {
          onLoadFormatRows(outputs);
        }
      } else if (effectiveFormat === "Flexible") {
        // Build all flexible ads: saved ones + current selection (if ≥2 selected)
        const allFlexibles: { selectedIds: string[] }[] = [...savedFlexibles.map(f => ({ selectedIds: f.selectedIds }))];
        if (selectedIds.size >= 2) {
          allFlexibles.push({ selectedIds: Array.from(selectedIds) });
        }

        if (allFlexibles.length === 0) return;

        const outputs: FlexibleRowOutput[] = await Promise.all(
          allFlexibles.map(async (flex, flexIdx) => {
            const selectedRows = flex.selectedIds
              .map(resolveRow)
              .filter(Boolean) as ModalRow[];

            const assets: FlexibleAsset[] = selectedRows.map((row) => {
              const primary = row.variants.feed || row.variants.vertical || row.variants.horizontal || row.members[0];
              return {
                id: primary?.id || "",
                meta_creative_id: primary?.meta_creative_id || "",
                filename: primary?.filename || "",
                thumbnail_url: primary?.thumbnail_url || null,
                type: (primary?.creative_type === "video" ? "video" : "image") as "image" | "video",
              };
            });

            const firstRow = selectedRows[0];
            const adName = await generateAdName({
              filename: firstRow?.displayName || "Flexible",
              adAccountId,
              userId,
              format: "Flexible",
              cardCount: selectedRows.length,
              seq: allFlexibles.length > 1 ? flexIdx + 1 : undefined,
            });

            const assetType = assets.some(a => a.type === "video") ? "video" as const : "image" as const;
            return {
              ad_format: "Flexible" as const,
              ad_name: adName,
              flexible_data: { assets, assetType },
              thumbnail_url: firstRow?.thumbnailUrl || null,
            };
          })
        );

        if (onLoadFormatRows) {
          onLoadFormatRows(outputs);
        }
      }

      // Reset and close
      setSelectedIds(new Set());
      setOrderedIds([]);
      setSavedCarousels([]);
      setSavedFlexibles([]);
      setSearch("");
      setTypeFilter("all");
      setCoverageFilter("all");
      onOpenChange(false);
    } finally {
      setIsConfirming(false);
    }
  }, [adAccountId, userId, selectedIds, orderedIds, savedCarousels, savedFlexibles, displayRows, effectiveFormat, selectionMode, onLoadRows, onLoadFormatRows, onOpenChange, ieLabel, ieProductHeadline, ieProductDescription]);

  // ===== Format label for footer =====
  const footerLabel = useMemo(() => {
    if (effectiveFormat === "Carousel") {
      if (savedCarousels.length === 0 && selectionCount === 0) return "Select creatives to load";
      const parts: string[] = [];
      savedCarousels.forEach((c) => parts.push(`${c.orderedIds.length} cards`));
      if (selectionCount >= 2) parts.push(`${selectionCount} cards`);
      if (parts.length === 0 && selectionCount > 0 && selectionCount < 2) {
        return `${selectionCount} selected — need at least 2 for a carousel`;
      }
      return `${parts.length} carousel${parts.length !== 1 ? "s" : ""} (${parts.join(" + ")})`;
    }
    if (effectiveFormat === "Collection") {
      if (selectionCount === 0) return "Select hero creatives";
      return `${selectionCount} selected → ${selectionCount} collection ad${selectionCount !== 1 ? "s" : ""}`;
    }
    if (effectiveFormat === "Flexible") {
      const parts: string[] = [];
      savedFlexibles.forEach((f) => parts.push(`${f.selectedIds.length} assets`));
      if (selectionCount >= 2) parts.push(`${selectionCount} assets`);
      const totalFlex = savedFlexibles.length + (selectionCount >= 2 ? 1 : 0);
      if (totalFlex === 0) {
        if (selectionCount > 0 && selectionCount < 2) return `${selectionCount} selected — need at least 2`;
        return "Select creatives to load";
      }
      return `${totalFlex} flexible ad${totalFlex !== 1 ? "s" : ""} (${parts.join(" + ")})`;
    }
    if (selectionCount === 0) return "Select creatives to load";
    return `${selectionCount} selected → ${selectionCount} row${selectionCount !== 1 ? "s" : ""}`;
  }, [selectionCount, effectiveFormat, savedCarousels, savedFlexibles]);

  const confirmLabel = useMemo(() => {
    if (isConfirming) return "Loading...";
    if (effectiveFormat === "Carousel") {
      const total = savedCarousels.length + (selectionCount >= 2 ? 1 : 0);
      if (total === 0) return "Load Carousel";
      return `Load ${total} Carousel${total !== 1 ? "s" : ""}`;
    }
    if (effectiveFormat === "Collection") {
      if (selectionCount <= 1) return "Load Collection";
      return `Load ${selectionCount} Collections`;
    }
    if (effectiveFormat === "Flexible") {
      const total = savedFlexibles.length + (selectionCount >= 2 ? 1 : 0);
      if (total <= 1) return "Load Flexible Ad";
      return `Load ${total} Flexible Ads`;
    }
    return `Load ${selectionCount} Row${selectionCount !== 1 ? "s" : ""}`;
  }, [isConfirming, effectiveFormat, selectionCount, savedCarousels.length, savedFlexibles.length]);

  // ===== Inline message below grid =====
  const inlineMessage = useMemo(() => {
    if (effectiveFormat === "Carousel" && selectionCount > 0 && selectionCount < 2 && savedCarousels.length === 0) {
      return "Select at least 2 creatives to create a carousel.";
    }
    if (effectiveFormat === "Flexible" && selectionCount > 0 && selectionCount < 2 && savedFlexibles.length === 0) {
      return "flexible-hint";
    }
    if (effectiveFormat === "Collection" && selectionCount === 0) {
      return "Select one or more hero creatives. Each becomes a collection ad.";
    }
    return null;
  }, [effectiveFormat, selectionCount, savedCarousels.length, savedFlexibles.length]);

  // ===== Non-blocking warnings (Carousel only) =====
  const [mixedMediaDismissed, setMixedMediaDismissed] = useState(false);

  const warnings = useMemo(() => {
    const msgs: { type: "warn" | "info"; text: string }[] = [];
    if (selectionCount === 0) return msgs;

    // Only show warnings for Carousel mode (grouped display)
    if (effectiveFormat !== "Carousel") return msgs;

    const selectedRows = orderedIds
      .map((id) => displayRows.find((r) => r.id === id))
      .filter(Boolean) as ModalRow[];

    // Mixed media info
    const hasImages = selectedRows.some((r) => r.type === "image");
    const hasVideos = selectedRows.some((r) => r.type === "video");
    if (hasImages && hasVideos && !mixedMediaDismissed) {
      msgs.push({ type: "info", text: "This carousel mixes images and videos — Meta supports this." });
    }
    // Inconsistent vertical coverage
    const withVertical = selectedRows.filter((r) => r.variants.vertical);
    const withoutVertical = selectedRows.filter((r) => !r.variants.vertical);
    if (withVertical.length > 0 && withoutVertical.length > 0) {
      const cardNums = withoutVertical.map((r) => orderedIds.indexOf(r.id) + 1).filter((n) => n > 0);
      msgs.push({ type: "warn", text: `Cards ${cardNums.join(", ")} don't have vertical variants — Stories/Reels delivery may be limited for those cards.` });
    }
    // Mixed: some vertical-only, others have square
    const verticalOnly = selectedRows.filter((r) => r.variants.vertical && !r.variants.feed);
    const withFeed = selectedRows.filter((r) => r.variants.feed);
    if (verticalOnly.length > 0 && withFeed.length > 0) {
      const cardNums = verticalOnly.map((r) => orderedIds.indexOf(r.id) + 1).filter((n) => n > 0);
      msgs.push({ type: "warn", text: `Card ${cardNums.join(", ")} is vertical-only while other cards have square variants — Meta may crop it to match.` });
    }

    return msgs;
  }, [selectionCount, orderedIds, displayRows, effectiveFormat, mixedMediaDismissed]);

  // ===== Handle confirm with >50 check for Single =====
  const handleConfirmClick = useCallback(() => {
    if (effectiveFormat === "Single Image or Video" && selectionCount > 50) {
      setShowBulkConfirm(true);
    } else {
      handleConfirm();
    }
  }, [effectiveFormat, selectionCount, handleConfirm]);

  const badgeClass = "text-xs font-normal border-border text-muted-foreground bg-transparent";

  return (
    <>
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
          <DialogTitle className="text-lg font-semibold">Load Creatives</DialogTitle>

          {/* Format selector pills */}
          <div className="flex flex-col gap-2 pt-3">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-muted-foreground mr-1">Ad Format:</span>
              {FORMAT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => handleFormatSwitch(opt.value)}
                  className={cn(
                    "px-3 py-1 rounded-full text-xs font-medium transition-colors border",
                    internalFormat === opt.value
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-transparent text-muted-foreground border-border hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              {FORMAT_HELPER_TEXT[internalFormat]}
            </p>
          </div>

          <div className="flex items-center gap-2 pt-3">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8 text-sm"
              />
            </div>

            <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as typeof typeFilter)}>
              <SelectTrigger className="w-[110px] h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="image">Image</SelectItem>
                <SelectItem value="video">Video</SelectItem>
              </SelectContent>
            </Select>

            {/* Hide coverage filter for Collection/Flexible since everything is individual */}
            {effectiveFormat !== "Collection" && effectiveFormat !== "Flexible" && (
              <Select value={coverageFilter} onValueChange={(v) => setCoverageFilter(v as typeof coverageFilter)}>
                <SelectTrigger className="w-[120px] h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Coverage</SelectItem>
                  <SelectItem value="Triple">Triple</SelectItem>
                  <SelectItem value="Pair">Pair</SelectItem>
                  <SelectItem value="Single">Single</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </DialogHeader>

        {/* Table */}
        <div className="min-h-0">
          {isLoading ? (
            <div className="p-4 space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                 <div key={i} className="flex items-center gap-3 px-3 py-3">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-[160px] w-[160px] rounded-lg" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-5 w-14 rounded-full" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Image className="h-10 w-10 text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground mb-1">No creatives available.</p>
              <p className="text-sm text-muted-foreground">
                Upload creatives to your library first.{" "}
                <a href="/creatives/upload" className="text-primary underline underline-offset-2 hover:text-primary/80">
                  Go to Upload
                </a>
              </p>
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="flex items-center bg-muted/80 backdrop-blur border-b border-border">
                <div className="w-10 px-3 py-2 shrink-0">
                  {selectionMode === "checkbox" && (
                    <Checkbox
                      checked={allFilteredSelected}
                      {...(someFilteredSelected ? { "data-state": "indeterminate" } : {})}
                      onCheckedChange={toggleSelectAll}
                    />
                  )}
                </div>
                <div className="w-[180px] px-2 py-2 text-xs font-medium text-muted-foreground shrink-0">Preview</div>
                <div className="flex-1 px-2 py-2 text-xs font-medium text-muted-foreground">Name</div>
                {/* Hide Variants/Coverage columns for individual-file modes */}
                {effectiveFormat !== "Collection" && effectiveFormat !== "Flexible" && (
                  <>
                    <div className="w-24 px-2 py-2 text-xs font-medium text-muted-foreground shrink-0">Variants</div>
                    <div className="w-16 px-2 py-2 text-xs font-medium text-muted-foreground shrink-0">Coverage</div>
                  </>
                )}
                <div className="w-20 px-2 py-2 text-xs font-medium text-muted-foreground shrink-0">Type</div>
                {(effectiveFormat === "Collection" || effectiveFormat === "Flexible") && (
                  <div className="w-24 px-2 py-2 text-xs font-medium text-muted-foreground shrink-0">Dimensions</div>
                )}
              </div>

              {/* Scrollable rows */}
              <div className="max-h-[600px] overflow-y-auto">
                {filtered.map((row) => {
                  const isSelected = selectedIds.has(row.id);
                  const carouselNumber = getCarouselNumber(row.id);
                  const isIndividualMode = effectiveFormat === "Collection" || effectiveFormat === "Flexible";

                  return (
                    <div
                      key={row.id}
                      onClick={() => toggleSelection(row.id)}
                      className={cn(
                        "flex items-center border-b border-border/50 cursor-pointer hover:bg-muted/40 transition-colors",
                        isSelected && "bg-primary/5"
                      )}
                    >
                      {/* Selection control */}
                      <div className="w-10 px-3 py-3 shrink-0 flex items-center">
                        {selectionMode === "checkbox" && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelection(row.id)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        )}
                        {selectionMode === "numbered" && (
                          <div
                            className={cn(
                              "h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold",
                              carouselNumber
                                ? "bg-primary text-primary-foreground"
                                : "border border-muted-foreground/40 text-muted-foreground"
                            )}
                          >
                            {carouselNumber || ""}
                          </div>
                        )}
                      </div>

                      {/* Thumbnail */}
                      <div className="w-[180px] px-2 py-2 shrink-0">
                        <div className="aspect-square w-[160px] rounded-lg bg-muted flex items-center justify-center overflow-hidden">
                          {row.thumbnailUrl ? (
                            <img src={row.thumbnailUrl} alt="" className="h-full w-full object-cover" />
                          ) : row.type === "video" ? (
                            <Video className="h-5 w-5 text-muted-foreground" />
                          ) : (
                            <Image className="h-5 w-5 text-muted-foreground" />
                          )}
                        </div>
                      </div>

                      {/* Name */}
                      <div className="flex-1 px-2 py-3 min-w-0">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="block truncate text-sm">{row.displayName}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p className="text-xs">{row.displayName}</p>
                              {row.members.length > 1 && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {row.members.map(m => m.filename).join(", ")}
                                </p>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>

                      {/* Variant badges — only for grouped modes */}
                      {!isIndividualMode && (
                        <div className="w-24 px-2 py-3 shrink-0 flex items-center gap-1">
                          <VariantBadge slot="feed" present={!!row.variants.feed} />
                          <VariantBadge slot="vertical" present={!!row.variants.vertical} />
                          <VariantBadge slot="horizontal" present={!!row.variants.horizontal} />
                        </div>
                      )}

                      {/* Type */}
                      <div className="w-20 px-2 py-3 shrink-0">
                        <Badge variant="outline" className={badgeClass}>
                          {row.type === "video" ? "Video" : "Image"}
                        </Badge>
                      </div>

                      {/* Coverage — only for grouped modes */}
                      {!isIndividualMode && (
                        <div className="w-16 px-2 py-3 shrink-0">
                          <Badge variant="outline" className={badgeClass}>
                            {row.coverage}
                          </Badge>
                        </div>
                      )}

                      {/* Dimensions — only for individual modes */}
                      {isIndividualMode && (
                        <div className="w-24 px-2 py-3 shrink-0">
                          <span className="text-xs text-muted-foreground">
                            {row.members[0]?.dimensions || "—"}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Inline message */}
        {inlineMessage && !isLoading && filtered.length > 0 && (
          <div className="px-6 py-2 border-t border-border/50">
            {inlineMessage === "flexible-hint" ? (
              <p className="text-xs text-muted-foreground">
                Flexible requires at least 2 assets. Add more or{" "}
                <button
                  type="button"
                  className="text-primary underline underline-offset-2 hover:text-primary/80"
                  onClick={() => {
                    setSelectedIds(new Set());
                    setOrderedIds([]);
                    setInternalFormat("Single Image or Video");
                  }}
                >
                  Switch to Single →
                </button>
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">{inlineMessage}</p>
            )}
          </div>
        )}

        {/* Carousel thumbnail strip — Fix 3: bigger thumbnails (h-20 w-20) */}
        {effectiveFormat === "Carousel" && orderedIds.length > 0 && (
          <div className="px-6 py-2 border-t border-border/50 flex items-center gap-2 overflow-x-auto">
            {orderedIds.map((id, idx) => {
              const row = displayRows.find((r) => r.id === id);
              return (
                <div key={id} className="relative shrink-0">
                  <div className="h-20 w-20 rounded-lg bg-muted overflow-hidden">
                    {row?.thumbnailUrl ? (
                      <img src={row.thumbnailUrl} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center">
                        <Image className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <span className="absolute -top-1.5 -left-1.5 h-5 w-5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center">
                    {idx + 1}
                  </span>
                </div>
              );
            })}
            {/* + Add Carousel button inline with thumbnails */}
            {orderedIds.length >= 2 && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => { e.stopPropagation(); handleAddCarousel(); }}
                className="shrink-0 h-8 text-xs gap-1"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Carousel
              </Button>
            )}
          </div>
        )}

        {/* Instant Experience settings for Collection mode */}
        {effectiveFormat === "Collection" && (
          <div className="px-6 py-3 border-t border-border/50">
            <p className="text-xs font-medium text-muted-foreground mb-2">Instant Experience Settings (applies to all)</p>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground mb-1 block">Label</label>
                <Input
                  value={ieLabel}
                  onChange={(e) => setIeLabel(e.target.value)}
                  placeholder="Shop Now"
                  className="h-8 text-sm"
                />
              </div>
              <div className="flex-1">
                <label className="text-xs text-muted-foreground mb-1 block">Product Headline</label>
                <Select value={ieProductHeadline} onValueChange={setIeProductHeadline}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLLECTION_HEADLINE_MACROS.map((m) => (
                      <SelectItem key={m.value} value={m.value} className="text-xs">
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="text-xs text-muted-foreground mb-1 block">Product Description</label>
                <Select value={ieProductDescription} onValueChange={setIeProductDescription}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COLLECTION_DESCRIPTION_MACROS.map((m) => (
                      <SelectItem key={m.value} value={m.value} className="text-xs">
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* + Add Flexible Ad button */}

        {effectiveFormat === "Flexible" && savedFlexibles.length > 0 && (
          <div className="px-6 py-2 border-t border-border/50 flex flex-wrap items-center gap-2">
            {savedFlexibles.map((f, i) => (
              <div
                key={f.id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-xs text-foreground border border-border"
              >
                <span className="font-medium">Flexible {i + 1}:</span>
                <span className="text-muted-foreground">{f.selectedIds.length} assets</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); handleRemoveSavedFlexible(f.id); }}
                  className="ml-0.5 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Warnings banner */}
        {warnings.length > 0 && (
          <div className="px-6 py-2 border-t border-border/50 space-y-1">
            {warnings.map((w, i) => (
              <div
                key={i}
                className={cn(
                  "flex items-start gap-1.5 text-xs",
                  w.type === "warn" ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground"
                )}
              >
                {w.type === "warn" ? (
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                ) : (
                  <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                )}
                <span>{w.text}</span>
                {w.type === "info" && (
                  <button
                    type="button"
                    onClick={() => setMixedMediaDismissed(true)}
                    className="ml-auto text-[10px] text-muted-foreground hover:text-foreground underline shrink-0"
                  >
                    Dismiss
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t border-border flex items-center justify-between sm:justify-between">
          <span className="text-sm text-muted-foreground">
            {footerLabel}
          </span>
          <Button
            onClick={handleConfirmClick}
            disabled={!canConfirm || isConfirming}
            size="sm"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* >50 rows confirmation dialog */}
    <AlertDialog open={showBulkConfirm} onOpenChange={setShowBulkConfirm}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Create {selectionCount} rows?</AlertDialogTitle>
          <AlertDialogDescription>
            You're about to create {selectionCount} rows. Continue?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => { setShowBulkConfirm(false); handleConfirm(); }}>
            Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Format switch confirmation dialog */}
    <AlertDialog open={!!pendingFormat} onOpenChange={(open) => !open && cancelFormatSwitch()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Switch format?</AlertDialogTitle>
          <AlertDialogDescription>
            Switching format will clear your current selection. Continue?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={cancelFormatSwitch}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirmFormatSwitch}>Switch</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
